import { v4 as uuid } from 'uuid'

import { TimeInterval, VideoEventType, VideoWatchEventData } from '~/behavior-tracking/types'
import { getPagePathOffset } from '~/behavior-tracking/utility/browser.utility'
import { getViewportId } from '~/behavior-tracking/utility/viewport-id.utility'
import { getEventKey } from '~/behavior-tracking/utility/helpers'
import videoEventsStore from '~/behavior-tracking/utility/video-events-store'

const EVENT_SCHEMA_VERSION = '1'

// Defensive guard on the "never emit a reversed range" invariant: normalize each
// [start, end] to [min, max] and drop zero/negative-length pairs. Native `video.played`
// is already spec-normalized (ordered, non-overlapping, start <= end), so this is
// belt-and-suspenders that holds regardless of the source of `ranges`.
export function normalizeRanges(ranges: TimeInterval[]): TimeInterval[] {
  return ranges
    .map(([start, end]): TimeInterval => [Math.min(start, end), Math.max(start, end)])
    .filter(([start, end]) => end > start)
}

function getPlayedRanges(videoElement: HTMLVideoElement): TimeInterval[] {
  const ranges: TimeInterval[] = []
  const { played } = videoElement
  const playIntervalCount = played.length

  // eslint-disable-next-line fp/no-loops
  for (let i = 0; i < playIntervalCount; i++) {
    const startMs = Math.round(played.start(i) * 1000)
    const endMs = Math.round(played.end(i) * 1000)

    ranges.push([startMs, endMs])
  }

  return ranges
}

export async function createVideoWatchEvent(userId: number, eventData: VideoWatchEventData): Promise<void> {
  const pagePathOffset = getPagePathOffset()

  const event = {
    ...eventData,
    extra: {
      ...(eventData.extra ?? {}),
      url: typeof document !== 'undefined' && document.URL.slice(pagePathOffset),
      schemaVersion: EVENT_SCHEMA_VERSION,
    },
    id: uuid(),
    createdAt: new Date().toISOString(),
    viewportId: getViewportId(),
  }

  await videoEventsStore.setItem(getEventKey(userId, event.id), event)
}

function isAnimationVideo(video: HTMLVideoElement): boolean {
  return video.muted && video.loop && video.autoplay
}

export function prepareVideoEvent(e: Event, eventName: VideoEventType, pagePath: string): VideoWatchEventData | null {
  const video: HTMLVideoElement | null = e.target as HTMLVideoElement

  if (video?.tagName !== 'VIDEO') {
    return null
  }

  const videoPath = video?.currentSrc
  const videoId = videoPath ? decodeURIComponent(videoPath).split('/').slice(3).join('/') : null
  const videoTimeSeconds = video?.currentTime

  if (videoId == null) {
    return null
  }

  if (isAnimationVideo(video)) {
    return null
  }

  const videoTimeMs = video?.currentTime != null ? Math.round(videoTimeSeconds * 1000) : null
  const durationMs = video?.duration != null ? Math.round(video?.duration * 1000) : null

  const isSkipEvent = eventName === VideoEventType.SKIP_EVENT

  const extra = {
    playedRanges: normalizeRanges(getPlayedRanges(video)),
    duration: durationMs,
    speed: video.playbackRate,
    muted: video.muted,
  }

  const finalEventName = isSkipEvent ? VideoEventType.PAUSE : eventName

  return {
    videoId,
    eventName: finalEventName,
    viewKey: pagePath,
    videoTime: videoTimeMs,
    extra,
  }
}
